/**
 * GenericResourceGroup - Generic collapsible group for sidebar resources
 *
 * This component handles:
 * - Expand/collapse state
 * - Filtering of root groups and ungrouped items
 * - Drag & drop (add to group, create group from items)
 * - Folder (ResourceGroup) creation
 * - Nested hierarchy via ResourceGroupItem
 *
 * This replaces the duplicated logic in TrackerGroup, EnrichmentGroup, AlertGroup, etc.
 */

import React, { useState, useMemo, useCallback, ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  ResourceGroup,
  ResourceType,
  getGroupRefId,
} from "@/models/resource-group";
import { useMultiSelect } from "@/hooks/use-multi-select";
import { useCreateResourceGroup } from "@/api/resource-group";
import { optimisticMutation, urlPredicate } from "@/utils/optimistic";
import {
  parseResourceDragPayload,
  RESOURCE_DRAG_TYPE,
} from "./resource-item-wrapper";
import SidebarGroupHeader, {
  SidebarGroupContent,
} from "./sidebar-group-header";
import ResourceGroupItem, { GroupableItem } from "./resource-group-item";

// =============================================================================
// Types
// =============================================================================

export interface GenericResourceGroupProps<T extends GroupableItem> {
  /** Group key (name/label for this group) */
  groupKey: string;
  /** Items in this group (filtered by the parent component) */
  items: T[];
  /** Current groupBy mode */
  groupBy: string;
  /** Resource type for this group */
  resourceType: ResourceType;

  // Rendering
  /** Render function for a single item */
  renderItem: (
    item: T,
    isSelected: boolean,
    orderedIds: string[],
    onMultiSelectContextMenu?: (e: React.MouseEvent) => void,
    onDropToCreateGroup?: (draggedId: string, targetId: string) => void,
  ) => ReactNode;
  /** Function to get the icon for a group based on groupKey and groupBy */
  getGroupIcon: (groupKey: string, groupBy: string, items: T[]) => ReactNode;

  // ResourceGroup support
  /** All resource groups for this resource type */
  resourceGroups?: ResourceGroup[];
  /** All items (for resource group filtering) */
  allItems?: T[];

  // Actions
  /** Mutation function to update an item's group */
  updateItemGroup?: (itemId: string, groupId: string) => Promise<void>;

  // Multi-select
  /** Ordered list of all IDs for range selection */
  orderedIds?: string[];
  /** Callback when right-clicking on a selected item */
  onMultiSelectContextMenu?: (e: React.MouseEvent) => void;

  // Options
  /** Whether the group is expanded by default */
  defaultExpanded?: boolean;
  /** Additional CSS classes */
  className?: string;
}

// =============================================================================
// Component
// =============================================================================

function GenericResourceGroup<T extends GroupableItem>({
  groupKey,
  items,
  groupBy,
  resourceType,
  renderItem,
  getGroupIcon,
  resourceGroups = [],
  allItems,
  updateItemGroup,
  orderedIds = [],
  onMultiSelectContextMenu,
  defaultExpanded = true,
  className = "",
}: GenericResourceGroupProps<T>): React.ReactElement {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [dragOverGroupId, setDragOverGroupId] = useState<string | null>(null);

  const queryClient = useQueryClient();
  const createGroupMutation = useCreateResourceGroup();

  // Multi-select hook
  const { isSelected } = useMultiSelect();

  const toggleExpand = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const currentItemIds = useMemo(() => new Set(items.map((i) => i.id)), [items]);

  // Filter resource groups to only show root-level groups that have
  // at least one descendant item in the current groupBy group.
  const rootGroups = useMemo(() => {
    const effectiveItems = allItems || items;

    const hasDescendantInGroup = (groupId: string): boolean => {
      if (
        effectiveItems.some(
          (item) =>
            getGroupRefId(item.group) === groupId && currentItemIds.has(item.id),
        )
      )
        return true;
      return resourceGroups
        .filter((g) => getGroupRefId(g.parent) === groupId)
        .some((child) => hasDescendantInGroup(child.id!));
    };

    return resourceGroups
      .filter((g) => !g.parent || !getGroupRefId(g.parent))
      .filter((g) => hasDescendantInGroup(g.id!))
      .sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [resourceGroups, items, allItems, currentItemIds]);

  // Build a set of all known group IDs for orphan detection
  const knownGroupIds = useMemo(
    () => new Set(resourceGroups.map((g) => g.id).filter(Boolean)),
    [resourceGroups]
  );

  // Filter items that are NOT in any existing resource group.
  // Includes items with orphaned group references (pointing to deleted groups).
  const ungroupedItems = useMemo(() => {
    return items.filter((item) => {
      const groupId = getGroupRefId(item.group);
      if (!groupId) return true;
      return !knownGroupIds.has(groupId);
    });
  }, [items, knownGroupIds]);

  // Invalidate queries after mutations
  const groupPred = urlPredicate("resource_group");
  const resourcePred = urlPredicate(resourceType);
  const invalidateQueries = useCallback(async () => {
    await queryClient.invalidateQueries({ predicate: groupPred });
    await queryClient.invalidateQueries({ predicate: resourcePred });
  }, [queryClient, resourceType]);

  // Create a new resource group (folder)
  const handleCreateGroup = useCallback(async () => {
    try {
      await createGroupMutation.mutateAsync({
        name: "Nouveau dossier",
        resource_type: resourceType,
        order: rootGroups.length,
      });
      invalidateQueries();
      if (!isExpanded) {
        setIsExpanded(true);
      }
    } catch (error) {
      console.error("Failed to create group:", error);
    }
  }, [
    createGroupMutation,
    invalidateQueries,
    isExpanded,
    rootGroups.length,
    resourceType,
  ]);

  // Handle drag over a resource group
  const handleDragOverGroup = useCallback(
    (e: React.DragEvent, groupId: string) => {
      e.preventDefault();
      setDragOverGroupId(groupId);
    },
    [],
  );

  // Handle drag leave
  const handleDragLeave = useCallback(() => {
    setDragOverGroupId(null);
  }, []);

  const resourcePredicate = urlPredicate(resourceType);

  // Handle drop on a resource group
  const handleDropOnGroup = useCallback(
    async (e: React.DragEvent, groupId: string) => {
      e.preventDefault();
      setDragOverGroupId(null);

      const resourceData = e.dataTransfer.getData(RESOURCE_DRAG_TYPE);
      const parsedData = parseResourceDragPayload(resourceData);

      if (
        parsedData &&
        parsedData.resourceType === resourceType &&
        updateItemGroup
      ) {
        try {
          await optimisticMutation({
            queryClient,
            queryPredicate: resourcePredicate,
            updater: (items: any) => {
              if (!Array.isArray(items)) return items;
              return items.map((item: any) =>
                item.id === parsedData.resourceId
                  ? { ...item, group: { id: groupId } }
                  : item,
              );
            },
            mutationFn: () => updateItemGroup(parsedData.resourceId, groupId),
            onSettled: () => invalidateQueries(),
          });
        } catch (error) {
          console.error("Failed to update item group:", error);
        }
      }
    },
    [queryClient, updateItemGroup, invalidateQueries, resourceType],
  );

  // Handle dropping an item on another item to create a group
  const handleDropToCreateGroup = useCallback(
    async (draggedItemId: string, targetItemId: string) => {
      const effectiveItems = allItems || items;
      const draggedItem = effectiveItems.find(
        (item) => item.id === draggedItemId,
      );
      const targetItem = effectiveItems.find(
        (item) => item.id === targetItemId,
      );

      if (!draggedItem || !targetItem) {
        console.error("Could not find items:", { draggedItemId, targetItemId });
        return;
      }

      // Check if both items are already in the same group
      const draggedGroupId = getGroupRefId(draggedItem.group);
      const targetGroupId = getGroupRefId(targetItem.group);

      try {
        // If both items are in the same group, create a sub-group under that group
        const parentGroupId =
          draggedGroupId === targetGroupId && draggedGroupId
            ? draggedGroupId
            : null;

        const newGroup = await createGroupMutation.mutateAsync({
          name: "Nouveau dossier",
          resource_type: resourceType,
          order: rootGroups.length,
          parent: parentGroupId || undefined,
        });

        if (newGroup?.id && updateItemGroup) {
          await Promise.all([
            updateItemGroup(draggedItemId, newGroup.id),
            updateItemGroup(targetItemId, newGroup.id),
          ]);
        }

        // Wait a bit for the backend to process the changes
        await new Promise((resolve) => setTimeout(resolve, 100));

        await invalidateQueries();

        if (!isExpanded) {
          setIsExpanded(true);
        }
      } catch (error) {
        console.error("Failed to create group from items:", error);
      }
    },
    [
      items,
      allItems,
      createGroupMutation,
      updateItemGroup,
      invalidateQueries,
      isExpanded,
      rootGroups.length,
      resourceType,
    ],
  );

  // Render an item using the provided render function
  const renderItemWrapper = useCallback(
    (item: T) =>
      renderItem(
        item,
        isSelected(item.id),
        orderedIds,
        onMultiSelectContextMenu,
        handleDropToCreateGroup,
      ),
    [
      renderItem,
      isSelected,
      orderedIds,
      onMultiSelectContextMenu,
      handleDropToCreateGroup,
    ],
  );

  const totalCount = rootGroups.length + ungroupedItems.length;
  const groupIcon = getGroupIcon(groupKey, groupBy, items);
  const isNoneMode = groupBy === "none";

  const folderItems = (
    <>
      {/* Resource Groups (folders) */}
      {rootGroups.map((group) => (
        <ResourceGroupItem<T>
          key={group.id}
          group={group}
          allItems={(allItems || items) as T[]}
          allGroups={resourceGroups}
          resourceType={resourceType}
          renderItem={renderItemWrapper}
          onDragOver={handleDragOverGroup}
          onDragLeave={handleDragLeave}
          onDrop={handleDropOnGroup}
          isDragOver={dragOverGroupId === group.id}
        />
      ))}

      {/* Ungrouped Items */}
      {ungroupedItems.map((item) => (
        <React.Fragment key={item.id}>
          {renderItem(
            item,
            isSelected(item.id),
            orderedIds,
            onMultiSelectContextMenu,
            handleDropToCreateGroup,
          )}
        </React.Fragment>
      ))}
    </>
  );

  // When groupBy is "none", render folders + items directly without a group header
  if (isNoneMode) {
    return (
      <div className={`group/resource-group ${className}`}>
        {folderItems}
      </div>
    );
  }

  return (
    <div className={`group/resource-group ${className}`}>
      <SidebarGroupHeader
        groupKey={groupKey}
        icon={groupIcon}
        count={totalCount}
        isExpanded={isExpanded}
        onToggle={toggleExpand}
        onCreateFolder={handleCreateGroup}
      />

      <SidebarGroupContent
        isExpanded={isExpanded}
        isEmpty={totalCount === 0}
        emptyMessage="Aucun élément"
      >
        {folderItems}
      </SidebarGroupContent>
    </div>
  );
}

GenericResourceGroup.displayName = "GenericResourceGroup";

export default GenericResourceGroup;

// =============================================================================
// Helper function to create update mutations
// =============================================================================

/**
 * Creates an updateItemGroup function from a mutation hook
 * @param mutateAsync - The mutateAsync function from the mutation hook
 * @param onSuccess - Optional callback to call after successful mutation
 */
export function createUpdateItemGroupFn(
  mutateAsync: (params: {
    group: string | { id: string };
    uriParams: { id: string };
  }) => Promise<unknown>,
  onSuccess?: () => void,
): (itemId: string, groupId: string) => Promise<void> {
  return async (itemId: string, groupId: string) => {
    const result = await mutateAsync({
      group: { id: groupId },
      uriParams: { id: itemId },
    });

    // Call onSuccess callback if provided
    if (onSuccess) {
      onSuccess();
    }
  };
}
